import React from 'react';
import { IconPlus, IconX } from '@tabler/icons-react';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import { Input } from '@/admin/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';

export type FilterBuilderOption = {
  key: string;
  label: string;
  type?: 'text' | 'date' | 'select';
  placeholder?: string;
  values?: Array<{ value: string; label: string }>;
};

export type ActiveFilterChip = {
  key: string;
  label: string;
  value: string;
  valueLabel?: string;
};

export function FilterBuilder({
  options,
  activeFilters,
  onAdd,
  onRemove,
  onClear,
  disabled,
}: {
  options: FilterBuilderOption[];
  activeFilters: ActiveFilterChip[];
  onAdd: (key: string, value: string) => void;
  onRemove: (key: string) => void;
  onClear?: () => void;
  disabled?: boolean;
}) {
  const [field, setField] = React.useState(options[0]?.key || '');
  const selectedOption = options.find(option => option.key === field) || options[0];
  const firstSelectValue = selectedOption?.values?.[0]?.value || '';
  const [value, setValue] = React.useState(firstSelectValue);

  React.useEffect(() => {
    const nextOption = options.find(option => option.key === field) || options[0];
    if (!nextOption) return;
    if (field !== nextOption.key) {
      setField(nextOption.key);
      return;
    }
    setValue(nextOption.type === 'select' ? nextOption.values?.[0]?.value || '' : '');
  }, [field, options]);

  const addFilter = () => {
    const trimmed = value.trim();
    if (!selectedOption || !trimmed) return;
    onAdd(selectedOption.key, trimmed);
    setValue(selectedOption.type === 'select' ? selectedOption.values?.[0]?.value || '' : '');
  };

  const availableOptions = options;

  React.useEffect(() => {
    if (availableOptions.length === 0) return;
    if (!availableOptions.some(option => option.key === field)) {
      setField(availableOptions[0].key);
    }
  }, [availableOptions, field]);

  const activeOption =
    availableOptions.find(option => option.key === field) || availableOptions[0];
  const inputDisabled = disabled || !activeOption;

  return (
    <div className="flex flex-wrap items-center gap-2">
        <Select
          value={activeOption?.key || ''}
          onValueChange={nextField => {
            const option = options.find(item => item.key === nextField);
            setField(nextField);
            setValue(option?.type === 'select' ? option.values?.[0]?.value || '' : '');
          }}
        >
          <SelectTrigger className="h-8 w-[180px]">
            <SelectValue placeholder="Choose filter" />
          </SelectTrigger>
          <SelectContent>
            {availableOptions.map(option => (
              <SelectItem key={option.key} value={option.key}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {activeOption?.type === 'select' ? (
          <Select value={value} onValueChange={setValue}>
            <SelectTrigger className="h-8 w-[200px]">
              <SelectValue placeholder={activeOption.placeholder || 'Value'} />
            </SelectTrigger>
            <SelectContent>
              {(activeOption.values || []).map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            type={activeOption?.type === 'date' ? 'date' : 'text'}
            value={value}
            onChange={event => setValue(event.currentTarget.value)}
            placeholder={activeOption?.placeholder || 'Value'}
            className="h-8 w-[280px]"
            disabled={inputDisabled}
          />
        )}
        <Button
          type="button"
          size="sm"
          onClick={addFilter}
          disabled={inputDisabled || !value.trim()}
          aria-label="Add filter"
        >
          <IconPlus className="size-4" />
        </Button>
        {activeFilters.length > 0 && onClear ? (
          <Button type="button" variant="ghost" size="sm" onClick={onClear} disabled={disabled}>
            Clear
          </Button>
        ) : null}
      {activeFilters.length > 0 ? (
        <>
          {activeFilters.map(filter => (
            <Badge key={filter.key} variant="secondary" className="gap-1 py-1 pl-2 pr-1">
              <span>{filter.label}: {filter.valueLabel || filter.value}</span>
              <button
                type="button"
                className="rounded-sm p-0.5 hover:bg-background/80"
                onClick={() => onRemove(filter.key)}
                aria-label={`Remove ${filter.label} filter`}
              >
                <IconX className="size-3" />
              </button>
            </Badge>
          ))}
        </>
      ) : null}
    </div>
  );
}
